import React, { ReactNode } from "react";
import {
  JourneyForm,
  JourneyOverview,
  JourneyStep,
} from "../../constants/AppointmentOverlayJourney";
import { formatDate } from "../../helper/dateFormatter";
import { useJustCompleted } from "./useJustCompleted";

// The checklist's row, with a status indicator rather than a toggle: these are
// completed by doing the work, not by ticking them off. A completed row still
// shows its action when it has one (e.g. "Start again"). A form that becomes
// done while the overlay is open (seen when the roadmap reloads) animates.
const Row = ({
  title,
  subtitle,
  isCompleted,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  isCompleted: boolean;
  action: ReactNode;
}) => {
  const justCompleted = useJustCompleted(isCompleted);
  return (
    <li
      className={`appointment-overlay__task${
        isCompleted ? " appointment-overlay__task--completed" : ""
      }${justCompleted ? " appointment-overlay__task--just-completed" : ""}`}
    >
      <span
        className="appointment-overlay__task-indicator appointment-overlay__task-indicator--static"
        aria-hidden="true"
      >
        {isCompleted && <span className="material-symbols-outlined">check</span>}
      </span>
      <div className="appointment-overlay__task-text">
        <div className="appointment-overlay__task-title">{title}</div>
        {subtitle && <div className="appointment-overlay__task-subtitle">{subtitle}</div>}
      </div>
      {action ?? (isCompleted && <span className="appointment-overlay__task-done">Completed</span>)}
    </li>
  );
};

const ActionButton = ({
  label,
  name,
  onClick,
}: {
  label: string;
  name: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`appointment-overlay__task-action${
      // Redoing finished work is the lesser path: plain text, like Exit.
      label === "Start again" ? " appointment-overlay__task-action--text" : ""
    }`}
    aria-label={`${label} ${name}`}
    onClick={onClick}
  >
    {label}
  </button>
);

const Header = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="appointment-overlay__body-header">
    <span className="appointment-overlay__body-title">{title}</span>
    {children}
  </div>
);

const HeaderLink = ({
  label,
  ariaLabel,
  onClick,
}: {
  label: string;
  ariaLabel?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="appointment-overlay__journey-link"
    aria-label={ariaLabel}
    onClick={onClick}
  >
    {label}
  </button>
);

/** How far along a step is, under its name; only while it's in progress. */
const stepProgress = (step: JourneyStep) =>
  step.status === "in-progress"
    ? step.forms.length > 1
      ? `${step.doneCount} of ${step.forms.length} done`
      : "In progress"
    : undefined;

/**
 * When a step was last worked, on the tile's right: "Last completed" over the
 * date once it's finished, "Last done" while it's in progress.
 */
const StepWhen = ({ step }: { step: JourneyStep }) => (
  <span className="appointment-overlay__choice-when">
    {step.lastDoneAt ? (
      <>
        <span className="appointment-overlay__choice-when-label">
          {step.status === "completed" ? "Last completed" : "Last done"}
        </span>
        <span className="appointment-overlay__choice-when-date">
          {formatDate(step.lastDoneAt)}
        </span>
      </>
    ) : (
      <span className="appointment-overlay__choice-when-label">
        {step.status === "completed" ? "Completed" : "Not done yet"}
      </span>
    )}
  </span>
);

// r=15 sits on the indicator's 2px ring.
const ARC = 2 * Math.PI * 15;

/**
 * The checklist's round indicator, reading a step's progress: an empty ring
 * before it's started, a teal arc for how much is done, and a solid check
 * once it's completed.
 */
const StepIndicator = ({ step }: { step: JourneyStep }) => {
  if (step.status === "completed") {
    return (
      <span
        className="appointment-overlay__choice-indicator appointment-overlay__choice-indicator--completed"
        aria-hidden="true"
      >
        <span className="material-symbols-outlined">check</span>
      </span>
    );
  }
  // An in-progress step with nothing submitted yet (a draft) still shows a sliver.
  const share =
    step.status === "in-progress"
      ? Math.max(step.doneCount / Math.max(step.forms.length, 1), 0.12)
      : 0;
  return (
    <span className="appointment-overlay__choice-indicator" aria-hidden="true">
      {share > 0 && (
        <svg viewBox="0 0 32 32">
          <circle
            cx="16"
            cy="16"
            r="15"
            style={{ strokeDasharray: ARC, strokeDashoffset: ARC * (1 - share) }}
          />
        </svg>
      )}
    </span>
  );
};

/**
 * A step in the chooser, as a tile like the checklist's rows: its progress
 * indicator, its name and tags, and where it stands. The current focus is
 * outlined in teal.
 */
const StepChoice = ({
  step,
  isUpNext,
  onChoose,
}: {
  step: JourneyStep;
  isUpNext: boolean;
  onChoose: () => void;
}) => (
  <li>
    {/* The current focus can be chosen too: it just keeps it and closes. */}
    <button
      type="button"
      className={`appointment-overlay__choice${
        isUpNext ? " appointment-overlay__choice--current" : ""
      }`}
      aria-label={`Choose ${step.name}`}
      aria-current={isUpNext || undefined}
      onClick={onChoose}
    >
      <StepIndicator step={step} />
      <span className="appointment-overlay__choice-text">
        <span className="appointment-overlay__choice-title">
          <span className="appointment-overlay__choice-name">{step.name}</span>
          {step.isSuggested && <span className="appointment-overlay__journey-tag">Suggested</span>}
          {step.isOptional && !step.isCompleted && (
            <span className="appointment-overlay__journey-tag appointment-overlay__journey-tag--quiet">
              Optional
            </span>
          )}
        </span>
        {stepProgress(step) && (
          <span className="appointment-overlay__choice-subtitle">{stepProgress(step)}</span>
        )}
      </span>
      <StepWhen step={step} />
    </button>
  </li>
);

/**
 * Which journey step is up next: the roadmap's suggestion until the clinician
 * picks another (finished ones included, to work them again). Picking the
 * suggestion follows it again, so it moves on with the roadmap. The choice is
 * kept by the caller (the overlay's store), so it survives a refresh; while
 * the chosen step isn't loaded yet, the suggestion stands in.
 */
export const useJourneyUpNext = (
  journey: JourneyOverview | null,
  chosenStepId: string | null,
  setChosenStepId: (stepId: string | null) => void
) => {
  const upNext =
    [...(journey?.steps ?? []), ...(journey?.extras ?? [])].find(
      (step) => step.id === chosenStepId
    ) ??
    journey?.suggested ??
    null;
  const choose = (step: JourneyStep) =>
    setChosenStepId(step.isSuggested ? null : step.id);
  return { upNext, choose };
};

/** A finished form's date, as a small grey badge: "Completed 10/01/2026". */
const CompletedBadge = ({ at }: { at: string | null }) => (
  <span className="appointment-overlay__completed-badge">
    Completed
    {at && <span className="appointment-overlay__completed-date">{formatDate(at)}</span>}
  </span>
);

const formSubtitle = (form: JourneyForm): ReactNode => {
  const kind = form.isCareNote ? "Care note" : null;
  if (form.status === "completed") {
    return (
      <>
        {kind && <span className="appointment-overlay__subtitle-kind">{kind}</span>}
        <CompletedBadge at={form.submittedAt} />
      </>
    );
  }
  return (
    [kind, form.status === "in-progress" ? "In progress" : null].filter(Boolean).join(" · ") ||
    undefined
  );
};

/**
 * The up-next step's forms as plain visit checklist rows (WCP-13254), so
 * their indicators line up with the tasks'. A care note says it's a care
 * note; an assessment's name stands on its own. They count toward the
 * card's "items left" ring.
 */
export const JourneyFormRows = ({
  forms,
  onOpen,
}: {
  forms: JourneyForm[];
  /** Navigates to a form to start or resume it. */
  onOpen: (to: string) => void;
}) => (
  <>
    {forms.map((form) => (
      <Row
        key={form.clinicalFormId}
        title={form.name}
        subtitle={formSubtitle(form)}
        isCompleted={form.status === "completed"}
        action={
          form.link && (
            <ActionButton
              label={form.link.label}
              name={form.name}
              onClick={() => onOpen(form.link!.to)}
            />
          )
        }
      />
    ))}
  </>
);

/**
 * In the checklist's header, across from its title: Change focus, to work a
 * different journey step.
 */
export const JourneyStepSwitch = ({
  onChange,
}: {
  /** Opens the step chooser in place of the card's body. */
  onChange: () => void;
}) => (
  <span className="appointment-overlay__journey">
    {/* A chip, not a text link, so it reads as a control for the whole list
        rather than another row action like "Start again". */}
    <button
      type="button"
      className="appointment-overlay__journey-link appointment-overlay__journey-link--chip"
      onClick={onChange}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        swap_horiz
      </span>
      Change focus
    </button>
  </span>
);

/**
 * Chooses what's next. Takes over everything below the card's header while
 * open, so the whole journey fits without crowding the checklist, and shows
 * where each step stands and when it was last worked.
 */
export const JourneyStepChooser = ({
  steps,
  extras = [],
  upNextId,
  onChoose,
  onCancel,
}: {
  steps: JourneyStep[];
  /** Other focuses (a Care Management note), listed after the journey below a divider. */
  extras?: JourneyStep[];
  upNextId: string | null;
  onChoose: (step: JourneyStep) => void;
  onCancel: () => void;
}) => (
  <div className="appointment-overlay__journey-chooser">
    <Header title="Choose focus">
      <HeaderLink label="Cancel" onClick={onCancel} />
    </Header>
    <ul className="appointment-overlay__choices" aria-label="Care journey steps">
      {steps.map((step) => (
        <StepChoice
          key={step.id}
          step={step}
          isUpNext={step.id === upNextId}
          onChoose={() => onChoose(step)}
        />
      ))}
      {extras.length > 0 && (
        <>
          {steps.length > 0 && (
            <li className="appointment-overlay__choices-divider" role="separator" />
          )}
          {extras.map((step) => (
            <StepChoice
              key={step.id}
              step={step}
              isUpNext={step.id === upNextId}
              onChoose={() => onChoose(step)}
            />
          ))}
        </>
      )}
    </ul>
  </div>
);
